import React, { useState, useEffect } from 'react';
import './ProductCombos.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ProductCombos = () => {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [comboItems, setComboItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // === THÊM MỚI: State cho Hero Image ===
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  // ====================================

  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchCombos();
  }, []);

  // === THÊM MỚI: useEffect để xử lý preview khi selectedCombo thay đổi ===
  useEffect(() => {
    if (selectedCombo && selectedCombo.hero_image) {
      // Nếu combo đã có hero_image, hiển thị nó
      setHeroImagePreview(getImageUrl(selectedCombo.hero_image));
    } else {
      setHeroImagePreview(null);
    }
    setHeroImageFile(null); // Reset file khi mở/đóng modal
  }, [selectedCombo]);
  // ===================================================================

  const getImageUrl = (localPath) => {
    if (!localPath) return '/images/empty_cart.webp';
    if (localPath.startsWith('http')) return localPath;
    if (localPath.startsWith('/uploads/')) {
      const baseUrl = API_URL.replace('/api', '');
      return `${baseUrl}${localPath}`;
    }
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/api/images/${localPath}`;
  };

  const fetchCombos = async () => {
    try {
      const token = localStorage.getItem('token'); // Lấy token
      const res = await fetch(`${API_URL}/product-combos/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` } // Thêm header Authorization
      });
      const data = await res.json();
      if (data.success) setCombos(data.data || []);
    } catch (err) {
      console.error('Failed to fetch combos', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchProducts = async () => {
    if (!searchTerm?.trim()) return;
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token'); // Lấy token
      const res = await fetch(
        `${API_URL}/product-groups/admin/search?title=${encodeURIComponent(searchTerm.trim())}`,
        {
          headers: { 'Authorization': `Bearer ${token}` } // Thêm header Authorization
        }
      );
      const data = await res.json();
      if (data.success) {
        // Lấy thông tin variants cho mỗi product group
        const productsWithVariants = await Promise.all((data.data || []).map(async p => {
          const variantRes = await fetch(`${API_URL}/product-groups/${p.id}`);
          const variantData = await variantRes.json();
          return {...p, variants: variantData.success? variantData.data.preview_variants : [] };
        }));
        setSearchResults(productsWithVariants);
      }
      else setSearchResults([]);
    } catch (err) {
      console.error('Search failed', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUpdateItem = (index, key, value) => {
    setComboItems(prev =>
      prev.map((item, i) => (i === index? {...item, [key]: value } : item))
    );
  };

  const handleRemoveItem = (index) => {
    setComboItems(prev => prev.filter((_, i) => i!== index));
  };

  // === THÊM MỚI: Hàm upload hero image cho combo ===
  const uploadComboHeroImage = async (comboId, file) => {
    if (!file ||!comboId) return;
    setHeroUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/product-groups/admin/${comboId}/hero-image`, { // Endpoint mới cho combo hero image
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setSelectedCombo(prev => prev? {...prev, hero_image: data.imageUrl } : null);
        fetchCombos(); // Refresh list combos
      } else {
        console.error('Failed to upload combo hero image:', data.error);
      }
    } catch (err) {
      console.error('Error uploading combo hero image:', err);
    } finally {
      setHeroUploading(false);
    }
  };
  // ===============================================

  const handleSaveCombo = async () => {
    if (!selectedCombo?.title?.trim()) {
      alert('Please enter combo title.');
      return;
    }
    if (isNaN(selectedCombo?.combo_price) || selectedCombo.combo_price <= 0) {
      alert('Please enter a valid combo price.');
      return;
    }
    if (comboItems.filter(item => item.product_group_id).length < 2) {
      alert('A combo must have at least 2 products.');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    const payload = {
      title: selectedCombo.title,
      description: selectedCombo.description || '',
      category_id: selectedCombo.category_id || null, // Assuming you might add category/brand selection later
      brand_id: selectedCombo.brand_id || null,
      combo_price: selectedCombo.combo_price,
      items: comboItems.map(item => ({
        product_group_id: item.product_group_id,
        // Chỉ gửi variant_id nếu nó có giá trị và không rỗng
       ...(item.variant_id && { variant_id: item.variant_id }),
        quantity: item.quantity
      }))
    };

    try {
      const url = selectedCombo.id
       ? `${API_URL}/product-combos/admin/${selectedCombo.id}`
        : `${API_URL}/product-combos/admin/create`;
      const method = selectedCombo.id? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        const comboId = selectedCombo.id || data.data.id; // Lấy ID combo vừa tạo hoặc ID combo hiện tại

        // === THÊM MỚI: Upload hero image sau khi combo được tạo/cập nhật ===
        if (heroImageFile && comboId) {
          await uploadComboHeroImage(comboId, heroImageFile);
        }
        // ===============================================================

        fetchCombos();
        setSelectedCombo(null);
        setComboItems([]);
        setHeroImageFile(null);
        setHeroImagePreview(null);
      } else {
        alert(data.error || 'Failed to save combo');
      }
    } catch (err) {
      console.error('Save combo failed', err);
      alert('Error saving combo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCombo = async (comboId) => {
    if (!window.confirm('Are you sure you want to delete this combo?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/product-combos/admin/${comboId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchCombos();
      else alert(data.error || 'Failed to delete combo');
    } catch (err) {
      console.error('Failed to delete combo', err);
      alert('Error deleting combo');
    }
  };

  const handleCancelEdit = () => {
    setSelectedCombo(null);
    setComboItems([]);
    setHeroImageFile(null);
    setHeroImagePreview(null);
    setSearchTerm(''); // Xóa kết quả tìm kiếm sản phẩm con
    setSearchResults([]);
  };

  if (loading) return <div>Loading combos...</div>;

  return (
    <div className="productCombosPage">
      <h2>Product Combos</h2>

      {/* Add New Combo Button */}
      <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
        <button
          className="addComboBtn"
          onClick={() => setSelectedCombo({ title: '', description: '', combo_price: 0, hero_image: '' })} // Khởi tạo hero_image rỗng
        >
          Add New Combo
        </button>
      </div>

      {/* Combo List */}
      <div className="comboGrid">
        {combos.map(combo => (
          <div key={combo.id} className="comboCard">
            <img
              src={combo.hero_image ? getImageUrl(combo.hero_image) : '/images/empty_cart.webp'} // Sửa đổi ở đây
              alt={combo.title}
              className="comboThumbnail"
              onError={(e) => { e.target.src = '/images/empty_cart.webp'; }}
            />
            <h3>{combo.title}</h3>
            <p>Price: ${parseFloat(combo.combo_price).toFixed(2)}</p>
            <div className="cardActions">
              <button className="delete" onClick={() => handleDeleteCombo(combo.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {selectedCombo && (
        <div className="modal">
          <div className="modalContent">
            <h3>{selectedCombo.id? 'Edit Combo' : 'Add New Combo'}</h3>

            <label>Title</label>
            <input
              type="text"
              value={selectedCombo.title || ''}
              onChange={e => setSelectedCombo(prev => ({...prev, title: e.target.value }))}
            />

            <label>Description</label>
            <textarea
              value={selectedCombo.description || ''}
              onChange={e => setSelectedCombo(prev => ({...prev, description: e.target.value }))}
            />

            <label>Combo Price</label>
            <input
              type="number"
              step="0.01" // Cho phép nhập số thập phân
              value={selectedCombo.combo_price === 0? '' : selectedCombo.combo_price} // Hiển thị rỗng nếu giá là 0
              onChange={e =>
                setSelectedCombo(prev => ({...prev, combo_price: e.target.value? parseFloat(e.target.value) : 0 }))
              }
            />

            {/* === THÊM MỚI: Phần upload Hero Image cho Combo === */}
            <div className="form-section">
              <label className="form-label">Hero Image</label>
              <div className="add-hero-upload">
                {heroImagePreview? (
                  <div className="add-hero-preview">
                    <img src={heroImagePreview} alt="Hero preview" />
                    <button
                      className="btn-remove-kit"
                      onClick={() => {
                        setHeroImageFile(null);
                        setHeroImagePreview(null);
                        setSelectedCombo(prev => ({...prev, hero_image: null })); // Xóa ảnh hero hiện tại nếu có
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <label className="btn-upload-image">
                    {heroUploading? 'Uploading...' : 'Choose Image'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setHeroImageFile(file);
                          setHeroImagePreview(URL.createObjectURL(file));
                        }
                      }}
                      disabled={heroUploading}
                    />
                  </label>
                )}
              </div>
            </div>
            {/* ================================================= */}

            {/* Product Search */}
            <div className="productSearch">
              <input
                type="text"
                placeholder="Search products to add..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button onClick={handleSearchProducts} disabled={searchLoading ||!searchTerm.trim()}>
                {searchLoading? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchResults.length > 0 && (
              <ul className="searchResults">
                {searchResults.map(product => (
                  <li key={product.id}>
                    <span class="product-name">{product.title} ({product.vendor_name || 'No Brand'})</span>
                    <button
                      onClick={() => {
                        // Kiểm tra xem sản phẩm đã có trong comboItems chưa
                        if (!comboItems.some(item => item.product_group_id === product.id)) {
                          setComboItems(prev => [
                           ...prev,
                            {
                              product_group_id: product.id,
                              variant_id: '', // Mặc định không chọn variant
                              quantity: 1,
                              product_title: product.title, // Lưu tên sản phẩm để hiển thị
                              product_variants: product.variants // Lưu variants của sản phẩm
                            }
                          ]);
                        }
                        setSearchResults([]);
                        setSearchTerm('');
                      }}
                      disabled={comboItems.some(item => item.product_group_id === product.id)} // Vô hiệu hóa nút Add nếu đã thêm
                    >
                      {comboItems.some(item => item.product_group_id === product.id)? 'Added' : 'Add'}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Combo Items */}
            <div className="comboItems">
              <h4>Combo Products ({comboItems.length} items)</h4>
              {comboItems.length === 0 && <p>No products added to combo yet.</p>}
              {comboItems.map((item, index) => {
                // Tìm thông tin sản phẩm từ searchResults hoặc từ item.product_title
                const productInfo = searchResults.find(p => p.id === item.product_group_id) || {
                  title: item.product_title || `Product ID: ${item.product_group_id}`,
                  variants: item.product_variants || []
                };

                return (
                  <div key={index} className="comboItemRow">
                    <span>{productInfo.title}</span>

                    <select
                      value={item.variant_id || ''}
                      onChange={e => handleUpdateItem(index, 'variant_id', e.target.value)}
                      disabled={!productInfo.variants || productInfo.variants.length === 0} // Vô hiệu hóa nếu không có variants
                    >
                      <option value="">Select Variant (Optional)</option>
                      {productInfo.variants?.map(v => (
                        <option key={v.id} value={v.id}>{v.attribute || v.size || v.sku || `Variant ${v.id}`}</option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={e => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)} // Đảm bảo số lượng luôn là số hợp lệ
                    />

                    <button onClick={() => handleRemoveItem(index)}>Remove</button>
                  </div>
                );
              })}
            </div>

            <div className="btnGroup">
              <button className="save" onClick={handleSaveCombo} disabled={saving || heroUploading}>
                {saving? 'Saving...' : 'Save Combo'}
              </button>
              <button className="cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCombos;